import { TextSelection } from 'prosemirror-state';
import { Fragment } from 'prosemirror-model';
import { canSplit } from 'prosemirror-transform';
import { getCollapsibleSectionChildPos, parentIsCollapsibleSectionSummary } from '../helpers/collapsible-section';
import { findParentNodeOfType } from 'prosemirror-utils';
export function splitBlockListItem(itemType) {
  return function (state, dispatch) {
    let {
      $from,
      $to
    } = state.selection;
    if (!isNestedBlock(state)) {
      return false;
    }
    if ($from.parent.type !== itemType) {
      return false;
    }
    if ($from.nodeAfter?.isBlock) {
      let positionAfterBlock = $to.after($to.depth);
      let tr = state.tr.insert(positionAfterBlock, itemType.createAndFill());
      let textSelection = TextSelection.create(tr.doc, positionAfterBlock + 2);
      if (dispatch) {
        dispatch(tr.setSelection(textSelection).scrollIntoView());
        return true;
      }
    }
    return false;
  };
}
export function splitEmptyTextListItem(itemType) {
  return function (state, dispatch) {
    let {
      $from
    } = state.selection;
    let grandParent = getGrandParent(state);
    if (!isNestedBlock(state)) {
      return false;
    }
    if (grandParent.type !== itemType) {
      return false;
    }
    if ($from.parent.content.size === 0 && grandParent.content.childCount > 1) {
      // An empty line within a list of many blocks
      if (!canSplit(state.tr.doc, $from.pos, 2)) {
        return false;
      }
      if (dispatch) {
        dispatch(state.tr.split($from.pos, 2).scrollIntoView());
      }
      return true;
    }
    return false;
  };
}

/**
 * When pressing enter in collapsibleSectionSummary Node, if the collapsible section is expanded,
 * the content should split and move to the start of collapsibleSectionContent.
 *
 * The text node in collapsible section summary (summaryTextNode) is sliced at the anchor position.
 * Everything from the anchor to the end of the text node (textSlice) is:
 * 1. deleted in the summaryTextNode
 * 2. inserted to the start of collapsible section content.
 *
 * Before (if the collapsible section is expanded):
 * > This is a |summary\
 *   Blah
 *
 * After:
 * > This is a\
 *   summary\
 *   Blah
 *
 * When pressing enter at the end of a collapsibleSectionSummary Node, if the collapsible section is collapsed,
 * insert a new paragraph node outside of the collapsible section.
 *  * Before (if the collapsible section is collapsed):
 * > This is a summary|
 *   Blah
 *
 * After:
 * > This is a summary
 *   Blah
 *
 *| is the cursor position
 */
export function splitCollapsibleSectionSummary(state, dispatch) {
  let {
    schema,
    selection,
    tr
  } = state;
  let $anchor = selection.$anchor;
  if (!parentIsCollapsibleSectionSummary(state)) {
    return false;
  }
  let collapsibleSection = findParentNodeOfType(schema.nodes.collapsibleSection)(selection);
  if (!collapsibleSection) {
    return false;
  }
  let open = collapsibleSection.node.attrs.open;
  let collapsed = !open;
  let summaryTextNode = state.selection.$from.parent;
  let anchorAtEndOfSummaryTextNode = $anchor.parentOffset === summaryTextNode.content.size;
  let insertNewParagraphOutsideCollapsibleSection = collapsed && anchorAtEndOfSummaryTextNode;
  let textStart = $anchor.pos;
  let textEnd = $anchor.pos + summaryTextNode.content.size - $anchor.parentOffset;
  // textSlice is what is being moved to the new text node
  // the positions passed to slice() are relative to summaryTextNode, not the entire document
  let textSlice = summaryTextNode.slice($anchor.parentOffset, summaryTextNode.content.size);
  let newTextNode = schema.nodes.paragraph.createAndFill({}, textSlice.content);
  let collapsibleSectionContentPos = getCollapsibleSectionChildPos(state, collapsibleSection, schema.nodes.collapsibleSectionContent);
  if (dispatch && newTextNode && !insertNewParagraphOutsideCollapsibleSection) {
    tr = tr.insert(collapsibleSectionContentPos.start(), newTextNode).replaceWith(textStart, textEnd, Fragment.empty);
    let textSelection = TextSelection.create(tr.doc, textStart + 4);
    tr.setSelection(textSelection).scrollIntoView();
    dispatch(tr);
    return true;
  }

  // if collapsed and the anchor at the end of the summaryTextNode,
  // we want to insert a new paragraph node outside of the collapsible section
  if (dispatch && insertNewParagraphOutsideCollapsibleSection) {
    let insertPos = collapsibleSection.pos + collapsibleSection.node.nodeSize;
    tr = tr.insert(insertPos, schema.nodes.paragraph.createAndFill({}, Fragment.empty));
    let textSelection = TextSelection.create(tr.doc, insertPos + 1);
    tr.setSelection(textSelection).scrollIntoView();
    dispatch(tr);
    return true;
  }
  return false;
}
function getGrandParent(state) {
  /**
   * When the value passed to .node(val) is less than 0
   * the position is offset from the current depth
   *
   * resolvedPos.node()   === resolvedPos.node(resolvedPos.depth) === resolvedPos.parent
   * resolvedPos.node(-1) === resolvedPos.node(resolvedPos.depth -1)
   **/
  let {
    $from
  } = state.selection;
  return $from.node(-1);
}
function isNestedBlock(state) {
  /**
   * depth :: number
   * The number of levels the parent node is from the root. If this
   * position points directly into the root node, it is 0. If it
   * points into a top-level paragraph, 1, and so on.
   * Any depth >= 2 is a nested node
   **/
  let {
    $from,
    $to
  } = state.selection;
  return $from.depth >= 2 && $from.sameParent($to);
}